import React, { Component } from 'react';
import classNames from 'classnames';
import T from 'prop-types';
import { log, callIfFunction } from '../Helpers/helpers';
import withGoogleMapsAPI from '../Containers/withGoogleMapsAPI';

function logError(e) {
  if (process.env.NODE_ENV === 'production') return;
  log(e);
}

class Map extends Component {
  BASE_CLASS = 'c-map';
  mapElement = React.createRef();

  state = {
    isMarkersLoading: false,
    initialBoundsSet: false,
  };
  listeners = [];
  map = null;
  googleAPI = null;
  markers = [];
  markerCluster = null;
  ticking = false;

  addListeners = this.addListeners.bind(this);
  setInitialBounds = this.setInitialBounds.bind(this);
  afterMapInit = this.afterMapInit.bind(this);

  getSizedIcon(markerOptions) {
    return {
      url: markerOptions.icon.url,
      scaledSize: new this.googleAPI.maps.Size(
        markerOptions.icon.width,
        markerOptions.icon.height
      ),
    };
  }

  createClusters() {
    const { clusterMarkerIcons, onClusterClick, markerOptions } = this.props;

    this.markerCluster = new window.MarkerClusterer(this.map, this.markers, {
      batchSize: 2000,
      styles: clusterMarkerIcons,
      clusterClass: 'pwt-marker-cluster',
      zoomOnClick: true,
    });

    const sizedIcon = this.getSizedIcon(markerOptions);

    // Because constructing markers with icons using an object for some reason is very slow,
    // do this only after clustering has ended, and only for the markers that are actually on
    // a map. See https://stackoverflow.com/questions/53429911/marker-load-time-is-10x-slower-when-icon-parameter-is-passed-valid-or-not .
    this.googleAPI.maps.event.addListener(
      this.markerCluster,
      'clusteringend',
      (cluster) => {
        cluster.getMarkers().forEach((m) => {
          if (m.map) {
            m.setIcon(sizedIcon);
          }
        });
      }
    );

    //Function that allows infomodal to show up on clusterclick when receptions are on the same address
    if (onClusterClick) {
      this.googleAPI.maps.event.addListener(
        this.markerCluster,
        'clusterclick',
        (cluster) => {
          onClusterClick(cluster.getMarkers(), cluster, this.map);
        }
      );
    }
  }

  addListeners(map) {
    return new Promise((resolve, reject) => {
      try {
        if (this.props.eventListeners) {
          // Add all event listeners to our ref
          Object.entries(this.props.eventListeners).forEach(([event, fn]) => {
            // Do not add listeners that we already have
            if (!this.listeners.find((o) => o.event === event)) {
              this.listeners.push({
                event,
                // map.addListener returns a listener instance.
                listener: map.addListener(event, () =>
                  fn({ map: map, api: this.googleAPI, markers: this.markers })
                ),
              });
            }
          });
        }
        resolve(map);
      } catch (e) {
        reject(e);
      }
    });
  }

  removeListeners() {
    if (!this.map || !this.googleAPI) return;
    this.listeners.forEach((o) => {
      this.googleAPI.maps.event.removeListener(o.listener);
    });
    this.listeners = [];
  }

  removeMarkers() {
    this.markers.forEach((marker) => {
      marker.setMap(null);
    });
    this.markers = [];

    if (this.markerCluster) this.markerCluster.clearMarkers();
    this.markerCluster = null;
  }

  createMarkers(map, locations, batchIteration) {
    return new Promise((resolve) => {
      this.processMarkerData(map, locations, batchIteration, resolve);
    });
  }

  processMarkerData(map, locations, batchIteration, resolve, bounds = null) {
    const { markerOptions, onMarkerClick } = this.props;

    if (!bounds) bounds = new this.googleAPI.maps.LatLngBounds();

    const batchedLocations = locations.splice(0, batchIteration);

    const shouldFitBounds = () => {
      if (this.props.initialBounds) {
        // When initial bounds are used we do not fit bounds.
        // TODO: Should only be used for first load. When we fetch pins with pagination bounds should be fitted.
        // Look at state.initalBoundsSet and a flag when fetching?
        return false;
      }
      return Boolean(
        this.props.Locations.length > 0 && !this.props.disableFitBounds
      );
    };

    if (!batchedLocations.length) {
      if (shouldFitBounds()) {
        //Set center to fit all markers
        this.map.setCenter(bounds.getCenter());
        this.map.fitBounds(bounds, 0);
        callIfFunction(this.props.afterFitBounds, this.map);
      }
      return resolve();
    }

    const sizedicon = this.getSizedIcon(markerOptions);

    requestAnimationFrame(() => {
      batchedLocations.forEach((locationData) => {
        if (!locationData.Latitude || !locationData.Longitude) return;

        const marker = this.createMarker(
          map,
          new this.googleAPI.maps.LatLng(
            locationData.Latitude,
            locationData.Longitude
          ),
          sizedicon,
          onMarkerClick,
          locationData
        );

        this.markers.push(marker);

        bounds.extend(marker.getPosition());
      });

      this.processMarkerData(map, locations, batchIteration, resolve, bounds);
    });
  }

  createMarker(map, position, sizedIcon, onClick, data) {
    const marker = new this.googleAPI.maps.Marker({
      position,
      data,
      map,
      icon: map ? sizedIcon : null,
    });

    if (onClick)
      marker.addListener('click', () => onClick(data, marker, this.map));

    return marker;
  }

  getCenter(lat, lng) {
    return lat && lng ? new this.googleAPI.maps.LatLng(lat * 1, lng * 1) : null;
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  initMap() {
    return new Promise((resolve) => {
      let { zoom, lat, lng, options } = this.props;
      const center = this.getCenter(lat, lng);
      if (!this.map) {
        this.map = new this.googleAPI.maps.Map(this.mapElement.current, {
          zoom: zoom,
          center: center,
          streetViewControl: false,
          mapTypeControl: false,
          ...options,
        });
      }
      return resolve(this.map);
    });
  }

  afterMapInit(map) {
    return new Promise((resolve) => {
      callIfFunction(this.props.onMapInit, { map, api: this.googleAPI });
      resolve(map);
    });
  }

  async UNSAFE_componentWillReceiveProps(nextProps) {
    if (!nextProps.google) return;

    this.googleAPI = nextProps.google;

    //Run load callback
    if (nextProps.google && !this.props.google && this.props.onGoogleAPILoad) {
      this.props.onGoogleAPILoad(this.googleAPI);
    }

    if (
      nextProps.Locations &&
      !this.state.isMarkersLoading &&
      (nextProps.Locations !== this.props.Locations ||
        this.props.async === false)
    ) {
      await this.initMap()
        .then(this.addListeners)
        .then(this.setInitialBounds)
        .then(this.afterMapInit)
        .catch(logError);

      this.setState({
        isMarkersLoading: true,
      });

      const markersPerIteration = 2000;

      const locationsCopy = [...nextProps.Locations];

      this.removeMarkers();

      //Create cluster markers else create markers
      if (this.props.useClusters) {
        await this.createMarkers(null, locationsCopy, markersPerIteration);

        if (this.props.clusterMarkerIcons) this.createClusters();
      } else {
        await this.createMarkers(this.map, locationsCopy, markersPerIteration);
      }

      this.setState({
        isMarkersLoading: false,
      });
    }

    if (
      !nextProps.Locations &&
      !this.props.useClusters &&
      this.props.lat &&
      this.props.lng
    ) {
      // We have no locations and will init the map with lat and lgn value only
      await this.initMap()
        .then(this.addListeners)
        .then(this.setInitialBounds)
        .then(this.afterMapInit)
        .catch(logError);

      const { lat, lng, markerOptions, onMarkerClick, markerData } = this.props;
      const center = this.getCenter(lat, lng);
      this.createMarker(
        this.map,
        center,
        this.getSizedIcon(markerOptions),
        onMarkerClick,
        markerData
      );
    }
  }

  setInitialBounds(map) {
    return new Promise((resolve, reject) => {
      try {
        if (this.props.initialBounds && !this.state.initialBoundsSet) {
          const { ne: initialNE, sw: initialSW } = this.props.initialBounds;
          const sw = new this.googleAPI.maps.LatLng(
            initialSW.latitude,
            initialSW.longitude
          );
          const ne = new this.googleAPI.maps.LatLng(
            initialNE.latitude,
            initialNE.longitude
          );

          const bounds = new this.googleAPI.maps.LatLngBounds(sw, ne);
          map.fitBounds(bounds, 0);

          this.setState({
            initialBoundsSet: true,
          });
        }
        return resolve(map);
      } catch (e) {
        log('warning')(e);
        return reject(e);
      }
    });
  }

  render() {
    const { className = this.BASE_CLASS, modifiers } = this.props;

    const componentClass = classNames({
      [`${className}`]: true,
      [`${className}--is-markers-loading`]: this.state.isMarkersLoading,
      ...modifiers,
    });

    const mapClass = `${className}__map`;

    return (
      <div className={componentClass}>
        <div ref={this.mapElement} className={mapClass} />
        {this.props.children && this.props.children(this.map, this.googleAPI)}
      </div>
    );
  }
}

Map.propTypes = {
  className: T.string,
};

Map.defaultProps = {
  onMapInit: () => {},
};

export default withGoogleMapsAPI(Map, '&libraries=geometry');
